import { useEffect, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { ClipboardCheck, Loader2, Save, Send } from "lucide-react";
import {
  canPublishDirectly,
  canSubmitForReview,
  getApprovalWorkflow,
  isWorkflowEnabled,
  type CatalogWorkflowAction,
  type PublicationStatus,
} from "@/lib/catalog/catalog-workflow";
import type { CatalogSettings } from "@/lib/api/catalog";

interface CatalogWorkflowButtonsProps {
  catalogSettings: CatalogSettings;
  loading?: boolean;
  isEditMode?: boolean;
  hideDraft?: boolean;
  publicationStatus?: PublicationStatus;
  isReadOnly?: boolean;
  createLabel?: string;
  onAction: (action: CatalogWorkflowAction) => void;
  layout?: "sidebar" | "inline";
}

export function CatalogWorkflowButtons({
  catalogSettings,
  loading = false,
  isEditMode = false,
  hideDraft = false,
  publicationStatus,
  isReadOnly = false,
  createLabel = "Catalog Book",
  onAction,
  layout = "sidebar",
}: CatalogWorkflowButtonsProps) {
  const [pending, setPending] = useState<CatalogWorkflowAction | null>(null);

  useEffect(() => {
    if (!loading) setPending(null);
  }, [loading]);

  if (isReadOnly) {
    return null;
  }

  const workflow = getApprovalWorkflow(catalogSettings);
  const workflowEnabled = isWorkflowEnabled(workflow);
  const showPublish = canPublishDirectly(workflow);
  const showSubmit = canSubmitForReview(workflow);
  const isDraftLike =
    !publicationStatus || publicationStatus === "DRAFT" || publicationStatus === "RETURNED";

  const publishLabel = isEditMode
    ? "Save Changes"
    : workflowEnabled && showPublish
      ? "Publish"
      : createLabel;

  const containerClass =
    layout === "sidebar" ? "space-y-2" : "flex flex-wrap items-center justify-end gap-2";

  const buttonClass = layout === "sidebar" ? "w-full gap-2" : "gap-2";
  const size = layout === "sidebar" ? ("lg" as const) : ("default" as const);

  const primaryAction: CatalogWorkflowAction = isEditMode ? "save-changes" : "publish";
  const showSaveDraft = !isEditMode && !hideDraft;
  const showSubmitButton = showSubmit && (!isEditMode || isDraftLike);
  const showPrimaryButton = isEditMode || showPublish || (!workflowEnabled && !showSubmitButton);

  const choose = (action: CatalogWorkflowAction) => {
    setPending(action);
    onAction(action);
  };

  const iconFor = (action: CatalogWorkflowAction, icon: ReactNode) =>
    loading && pending === action ? <Loader2 className="h-4 w-4 animate-spin" /> : icon;

  return (
    <div className={containerClass}>
      {showSaveDraft ? (
        <Button
          type="button"
          variant="outline"
          size={size}
          className={buttonClass}
          disabled={loading}
          onClick={() => choose("draft")}
        >
          {iconFor("draft", <Save className="h-4 w-4" />)}
          Save as Draft
        </Button>
      ) : null}

      {showSubmitButton ? (
        <Button
          type="button"
          size={size}
          className={buttonClass}
          disabled={loading}
          onClick={() => choose("submit-for-review")}
        >
          {iconFor("submit-for-review", <Send className="h-4 w-4" />)}
          Submit for Review
        </Button>
      ) : null}

      {showPrimaryButton ? (
        <Button
          type="button"
          size={size}
          className={buttonClass}
          disabled={loading}
          onClick={() => choose(primaryAction)}
        >
          {iconFor(primaryAction, <ClipboardCheck className="h-4 w-4" />)}
          {publishLabel}
        </Button>
      ) : null}
    </div>
  );
}
